import {
    Breakpoints,
    CTA,
    Image,
    TextTag,
    Container,
    TokenTextColor,
} from '@volkswagen-onehub/components-core'
import {globalColors, SizeEnum} from '@volkswagen-onehub/tokens'
import styled from 'styled-components'
import React from 'react'
import isEmpty from 'lodash.isempty'
import {SingleSimulation} from '../../types/SingleSimulation'
import {SimulationAccordion} from '../components/SimulationAccordion'
import {Filter} from '@volkswagen-onehub/icons-core'
import {useTranslation} from 'react-i18next'
import {
    showSimulationByAdvance,
    showSimulationByMonthlyRange,
} from '../../shared/utilities'
import {ModelName} from '../components/ModelName'
import {
    eventInfoCommon,
    TrackingAction,
    trackingEvent,
} from '../../shared/trackingServiceHelper'

const SingleModelContainer = styled.div`
    margin-bottom: ${(props) => props.theme.size.dynamic0250};

    @media (min-width: ${Breakpoints.b560}px) {
        display: grid;
        grid-template-areas:
            'modelTitle modelImage'
            'modelSimulations modelImage';
        grid-template-columns: 1fr 1fr;
        grid-column-gap: ${(props) => props.theme.size.grid001};
    }
`
const TitleContainer = styled(Container)`
    grid-area: modelTitle;
`

const ImageContainer = styled.div`
    grid-area: modelImage;

    @media (min-width: ${Breakpoints.b560}px) {
        display: flex;
        justify-content: end;
    }
`

const SimulationsContainer = styled.ul`
    grid-area: modelSimulations;
    list-style: none;
    margin: 0;
    padding: 0;
`

const ImageBackground = styled.div`
    position: relative;
    height: ${(props) => props.theme.size.static1000};
    padding-top: ${(props) => props.theme.size.dynamic0050};
    padding-left: ${(props) => props.theme.size.dynamic0050};
    padding-right: ${(props) => props.theme.size.dynamic0050};
    margin-bottom: ${(props) => props.theme.size.static575};
    background: ${globalColors.newHorizon[40]};

    @media (min-width: ${Breakpoints.b560}px) {
        height: 50%;
        width: 50%;
        padding: 2.8rem 0 0;
        margin-left: ${(props) => props.theme.size.static800};
        margin-bottom: ${(props) => props.theme.size.static800};
    }

    @media (min-width: ${Breakpoints.b960}px) {
        padding: 18rem 0 0;
        margin-bottom: ${(props) => props.theme.size.static500};
    }

    @media (min-width: ${Breakpoints.b1280}px) {
        padding: 25rem 0 0;
        margin-bottom: ${(props) => props.theme.size.static200};
    }
`
const ModelImage = styled(Image)`
    position: absolute;
    width: 100%;
    bottom: -25%;
    left: 0;

    @media (min-width: ${Breakpoints.b560}px) {
        bottom: 0;
        left: -100%;
        width: 200%;
    }
`

const FiltersCtaContainer = styled.div`
    button {
        margin: 0 auto;
        display: block;

        @media (min-width: ${Breakpoints.b560}px) {
            margin: 0;
        }
    }
`

interface SingleModelViewProps {
    modelData: any
}

export function SingleModelView(
    props: SingleModelViewProps
): React.JSX.Element {
    const {t} = useTranslation('fa-flotte')

    const scrollToFilters = () => {
        const filtersBar = document.getElementById('filtersBar')

        if (filtersBar) {
            filtersBar.scrollIntoView({behavior: 'smooth'})
        }
    }

    return (
        <Container wrap="always">
            <SingleModelContainer>
                {!isEmpty(props.modelData) && (
                    <>
                        <TitleContainer
                            padding={{
                                bottom: SizeEnum.static520,
                            }}
                        >
                            <ModelName
                                model={props.modelData.name}
                                bigger={true}
                                tag={TextTag.h3}
                                color={TokenTextColor.primary}
                            />
                        </TitleContainer>
                    </>
                )}

                {!isEmpty(props.modelData.data) && (
                    <>
                        <ImageContainer>
                            <ImageBackground>
                                <ModelImage
                                    src={props.modelData.imageRotate}
                                    alt={props.modelData.name}
                                />
                            </ImageBackground>
                        </ImageContainer>

                        <SimulationsContainer>
                            {props.modelData.data.simulations.map(
                                (promo: SingleSimulation, index: number) =>
                                    showSimulationByAdvance(promo) &&
                                    showSimulationByMonthlyRange(promo) ? (
                                        <SimulationAccordion
                                            key={index}
                                            index={index}
                                            modelName={props.modelData.name}
                                            promoData={promo}
                                            infoUrl={
                                                props.modelData.data
                                                    .info_cta_url
                                            }
                                            image={props.modelData.imageListing}
                                            singleModel={true}
                                            infoUrlPassThrough={
                                                props.modelData.data
                                                    .info_cta_url_params_pass_through
                                            }
                                        />
                                    ) : undefined
                            )}
                        </SimulationsContainer>
                    </>
                )}
            </SingleModelContainer>
            <FiltersCtaContainer>
                <CTA
                    tag="button"
                    emphasis="tertiary"
                    onClick={() => {
                        scrollToFilters()

                        trackingEvent(TrackingAction.VW_BASIC_LINK_CLICK, {
                            eventInfo: {
                                ...eventInfoCommon,
                                contentId: t('cta.moreFilters'),
                            },
                            link: {
                                name: t('cta.moreFilters'),
                                newTab: false,
                                url: window.location.href,
                            },
                        })
                    }}
                    aria-label={t('cta.moreFilters')}
                    icon={<Filter aria-hidden />}
                    iconPosition="start"
                >
                    {t('cta.moreFilters')}
                </CTA>
            </FiltersCtaContainer>
        </Container>
    )
}
